import { CloudUpload } from "lucide-react";
import type { ClipboardEvent, DragEvent } from "react";
import { useRef, useState } from "react";

import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";

export interface FileDropzoneProps {
  onFiles: (files: File[]) => void;
  /** Native `accept` attribute (e.g. "application/pdf,image/*"). Also enforced on drop/paste. */
  accept?: string;
  /** Max size per file, in bytes. Oversized files are rejected with a toast. */
  maxSize?: number;
  multiple?: boolean;
  /** Secondary helper line under the main label. */
  hint?: string;
  className?: string;
}

/** Loose accept matcher for dropped/pasted files (the file input enforces it natively). */
function matchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept) {
    return true;
  }
  return accept.split(",").some((rawToken) => {
    const token = rawToken.trim().toLowerCase();
    if (!token) {
      return true;
    }
    if (token.startsWith(".")) {
      return file.name.toLowerCase().endsWith(token);
    }
    if (token.endsWith("/*")) {
      return file.type.toLowerCase().startsWith(token.slice(0, -1));
    }
    return file.type.toLowerCase() === token;
  });
}

function formatMaxSize(maxSize: number): string {
  const megabytes = maxSize / (1024 * 1024);
  return `${megabytes % 1 === 0 ? megabytes : megabytes.toFixed(1)} Mo`;
}

/**
 * Drag-drop + click-to-browse + paste file picker — React counterpart of
 * files/FileInput.vue (selection only; the file list display is FileCard's job).
 */
export function FileDropzone({
  onFiles,
  accept,
  maxSize,
  multiple = false,
  hint,
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  function acceptFiles(candidates: File[]) {
    const files: File[] = [];
    for (const file of candidates) {
      if (!matchesAccept(file, accept)) {
        notify.error(`Type de fichier non supporté : ${file.name}`);
        continue;
      }
      if (maxSize !== undefined && file.size > maxSize) {
        notify.error(`Fichier trop volumineux : ${file.name}`, {
          description: `Taille maximale : ${formatMaxSize(maxSize)}`,
        });
        continue;
      }
      files.push(file);
    }
    if (!files.length) {
      return;
    }
    onFiles(multiple ? files : files.slice(0, 1));
  }

  function onDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsDragOver(false);
    acceptFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  function onPaste(event: ClipboardEvent<HTMLButtonElement>) {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length) {
      event.preventDefault();
      acceptFiles(files);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
        onPaste={onPaste}
        className={cn(
          "flex w-full flex-col items-center justify-center rounded-xl border-[1.5px] border-dashed px-4 py-4 text-center transition-all hover:border-blue-400 hover:bg-gradient-to-b hover:from-zinc-50 hover:to-blue-50",
          isDragOver ? "border-blue-600 bg-blue-50" : "border-gray-300 bg-slate-50",
          className,
        )}
      >
        <CloudUpload className={cn("my-2 h-8 w-8 text-neutral-600", isDragOver && "text-blue-500")} />
        <p className="text-sm font-semibold text-gray-900">
          Glissez-déposez ou cliquez pour ajouter un fichier
        </p>
        {hint ? <p className="mt-1 text-sm text-neutral-500">{hint}</p> : null}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={(event) => {
          acceptFiles(Array.from(event.currentTarget.files ?? []));
          // Allow re-selecting the same file after a removal.
          event.currentTarget.value = "";
        }}
      />
    </>
  );
}
